import { differenceInCalendarDays, parseISO, isToday } from 'date-fns';

import type { Season } from '@/types/season';

/** Sit and harvest totals for a season — used by the countdown hero for in-season and post-season states. */
export interface SeasonHuntStats {
  totalSits: number;
  morningSits: number;
  eveningSits: number;
  totalHarvests: number;
  /** Unique calendar dates on which at least one sit occurred. */
  daysAfield: number;
}

/**
 * Days from today to the opener date.
 * Positive = future, 0 = today, negative = past.
 */
export function daysUntilOpener(openerDate: string): number {
  const opener = parseISO(openerDate);
  // Use a UTC-midnight anchor so date-only strings (parsed as UTC midnight by
  // date-fns v4 parseISO) compare consistently, regardless of local timezone.
  const todayUtc = parseISO(new Date().toISOString().slice(0, 10));
  return differenceInCalendarDays(opener, todayUtc);
}

export type SeasonStatus = 'upcoming' | 'active' | 'ended';

/**
 * Derives the current status of a season based on today's date.
 * - upcoming: opener is in the future (or today before season)
 * - active: today is between opener and closer (inclusive), or opener is today/past with no closer
 * - ended: closer date is in the past
 */
export function seasonStatus(season: Season): SeasonStatus {
  const today = new Date().toISOString().slice(0, 10);
  const opener = season.openerDate;
  const closer = season.closerDate;

  if (closer && closer < today) return 'ended';
  if (opener <= today) return 'active';
  return 'upcoming';
}

/**
 * Human-readable countdown string.
 * e.g. "47 days", "Today", "3 days ago"
 */
export function formatCountdown(days: number): string {
  if (days === 0) return 'Today';
  if (days > 0) return `${days} day${days === 1 ? '' : 's'}`;
  const abs = Math.abs(days);
  return `${abs} day${abs === 1 ? '' : 's'} ago`;
}

/**
 * Formats "IN SEASON · Day X" where X is days since opener.
 */
export function formatInSeasonLabel(openerDate: string): string {
  const dayNum = Math.abs(daysUntilOpener(openerDate));
  // Day 1 = opener day
  const day = isToday(parseISO(openerDate)) ? 1 : dayNum + 1;
  return `IN SEASON · Day ${day}`;
}
